import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import isNull from 'lodash/isNull';
import { getKeyCancellationFromPath, RootState, LoginType } from '@share/utils';
import { getReservationCancellationDetails } from '@share/store/slices';
import { SkeletonReviewBook } from '../../common';
import { ReservationCancellation } from '../reservation-cancellation-info';
import { ReservationCancellationRoomInfo } from '../reservation-cancellation-room-info';
import {
  ErrorRequestCancel,
  CancelledBooking,
  CancellationFailed,
  CancellationNotAvailable,
  CancellationUnknown,
} from '../notification';

import './style.scss';

interface IProps {
  isVacationRentals?: boolean;
}

const ZERO = 0;

export function ReservationCancellationWrapper(props: IProps) {
  const { isVacationRentals } = props;

  const dispatch = useDispatch();

  const reservationCancellationStore = useSelector((state: RootState) => state.reservationCancellationStore);
  const loginStore = useSelector((state: RootState) => state.loginStore);
  const menuStore = useSelector((state: RootState) => state.navigationMenuStore);

  const { account, user } = loginStore;
  const { reservationCancellationDetails, loading, error, cancelReservation } = reservationCancellationStore;

  useEffect(() => {
    if (!!user) {
      const tempKey = getKeyCancellationFromPath();
      dispatch(getReservationCancellationDetails(tempKey));
    }
  }, [user]);

  const isPrivateWithToken = account?.type !== LoginType.PrivateWithToken;
  const privateTokenFromUrl = account?.privateTokenFromUrl;

  const reservationDetails = reservationCancellationDetails?.reservationDetails;
  const bookingType = reservationDetails?.bookingType;
  const venueDetails =
    bookingType === 'Activity' ? reservationDetails?.activity : reservationDetails?.hotel;
  const venueName = venueDetails?.name ?? '';
  const venueCity = venueDetails?.city ?? '';
  const venueState = venueDetails?.state ?? '';
  const supportPhone = reservationDetails?.supportPhone ?? '';

  return (
    <div className="reservation-cancellation-wrapper">
      {loading ? <SkeletonReviewBook /> : null}
      {reservationCancellationDetails && !loading ? (
        <>
          {isNull(cancelReservation) && reservationCancellationDetails.isCancellable ? (
            <div className="reservation-cancellation-wrapper__header">
              <h4 className="reservation-cancellation-wrapper__title secondary-font">
                <FormattedMessage id="review.cancel" />
              </h4>
              <p className="reservation-cancellation-wrapper__description">
                <FormattedMessage id="review.reservation.cancellation" />
              </p>
            </div>
          ) : null}
          <div
            className={`reservation-cancellation-wrapper__wrapper
            ${!reservationDetails ? 'flex-center' : ''}`}
          >
            {isNull(cancelReservation) ? (
              reservationCancellationDetails.isCancellable ? (
                <ReservationCancellation />
              ) : reservationCancellationDetails.isExpired ? (
                <CancellationNotAvailable
                  isExpired={reservationCancellationDetails.isExpired}
                  isMemberType={isPrivateWithToken && menuStore.error.length === ZERO}
                  isVacationRentals={isVacationRentals}
                  isExternal={isPrivateWithToken && !privateTokenFromUrl}
                />
              ) : reservationCancellationDetails.isTryAgainLater ? (
                <CancellationFailed
                  isTryAgainLater={reservationCancellationDetails.isTryAgainLater}
                  supportPhone={supportPhone}
                />
              ) : (
                <CancellationNotAvailable
                  isMemberType={isPrivateWithToken && menuStore.error.length === ZERO}
                  isVacationRentals={isVacationRentals}
                  isExternal={isPrivateWithToken && !privateTokenFromUrl}
                />
              )
            ) : (
              <>
                {cancelReservation?.isCompletedSuccessfully ? (
                  <CancelledBooking
                    city={venueCity}
                    name={venueName}
                    state={venueState}
                    isMemberType={isPrivateWithToken && menuStore.error.length === ZERO}
                    isExternal={isPrivateWithToken && !privateTokenFromUrl}
                  />
                ) : cancelReservation?.isPending ? (
                  <CancellationUnknown supportPhone={supportPhone} />
                ) : cancelReservation?.isExpired ? (
                  <CancellationNotAvailable
                    isExpired={reservationCancellationDetails.isExpired ?? false}
                    isMemberType={isPrivateWithToken && menuStore.error.length === ZERO}
                    isVacationRentals={isVacationRentals}
                    isExternal={isPrivateWithToken && !privateTokenFromUrl}
                  />
                ) : cancelReservation?.isTryAgainLater ? (
                  <CancellationFailed
                    isTryAgainLater={cancelReservation.isTryAgainLater}
                    supportPhone={supportPhone}
                  />
                ) : !cancelReservation?.isCompletedSuccessfully &&
                  !cancelReservation?.isPending ? (
                  <CancellationUnknown supportPhone={supportPhone} />
                ) : null}
              </>
            )}
            {reservationDetails && (
              <ReservationCancellationRoomInfo
                hotelDetails={reservationDetails}
                isPromo={menuStore.items?.promo ?? false}
              />
            )}
          </div>
        </>
      ) : null}
      {error.length !== ZERO && !loading ? <ErrorRequestCancel /> : null}
    </div>
  );
}
